import {skipToken} from '@reduxjs/toolkit/query';
import {Action, BreadcrumbType, openDeleteDialog, showNotification} from 'platform/components';

import {useParams} from 'react-router-dom';

import {isNotNil} from 'ramda';

import {
  useActivateServiceAccountMutation,
  useDeactivateServiceAccountMutation,
  useDeleteServiceAccountMutation,
  useGetServiceAccountQuery,
} from '@dms/api/security';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {handleApiError, SectionTab, usePermissions} from '@dms/shared';

import {buildArray, useNavigate} from 'shared/web';

import {ServiceAccountAuditLogTab} from './components/ServiceAccountAuditLogTab';
import {ServiceAccountGeneralTab} from './components/ServiceAccountGeneralTab';
import {ServiceAccountKeysTab} from './components/ServiceAccountKeysTab';
import {ServiceAccountRequestLogTab} from './components/ServiceAccountRequestLogTab';

const breadcrumbs: BreadcrumbType[] = [
  {
    label: i18n.t('entity.serviceAccount.labels.serviceAccounts'),
    href: settingsRoutes.serviceAccountManagement,
  },
];

export function ServiceAccountDetail() {
  const navigate = useNavigate();
  const {serviceAccountId} = useParams<{serviceAccountId: string}>();

  const {
    data: serviceAccount,
    isLoading,
    isError,
  } = useGetServiceAccountQuery(isNotNil(serviceAccountId) ? {serviceAccountId} : skipToken);

  const [activateServiceAccount] = useActivateServiceAccountMutation();
  const [deactivateServiceAccount] = useDeactivateServiceAccountMutation();
  const [deleteServiceAccount] = useDeleteServiceAccountMutation();

  const [canActivate, canDeactivate, canDelete, canRotate, canRevoke] = usePermissions({
    permissionKeys: [
      'activateServiceAccount',
      'deactivateServiceAccount',
      'deleteServiceAccount',
      'rotateServiceAccountKey',
      'revokeServiceAccountKey',
    ],
  });

  const handleActivate = (id: string) =>
    activateServiceAccount({serviceAccountId: id})
      .unwrap()
      .then(() => showNotification.success())
      .catch(handleApiError);

  const handleDeactivate = (id: string) =>
    deactivateServiceAccount({serviceAccountId: id})
      .unwrap()
      .then(() => showNotification.success())
      .catch(handleApiError);

  const handleDelete = (id: string, name: string) =>
    openDeleteDialog({
      text: i18n.t('entity.serviceAccount.labels.deleteDialog', {name}),
      'data-testid': testIds.settings.serviceAccountManagementDetail('deleteDialog'),
      onConfirm: () =>
        deleteServiceAccount({serviceAccountId: id})
          .unwrap()
          .then(() => navigate(settingsRoutes.serviceAccountManagement))
          .catch(handleApiError),
    });

  const actions = isNotNil(serviceAccount)
    ? buildArray<Action>()
        .when(canDeactivate && serviceAccount.status === 'active', {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('entity.serviceAccount.actions.deactivate'),
          onClick: () => handleDeactivate(serviceAccount.id),
          'data-testid': testIds.settings.serviceAccountManagementDetail('deactivate'),
        })
        .when(canActivate && serviceAccount.status === 'inactive', {
          type: 'button',
          variant: 'secondary',
          title: i18n.t('entity.serviceAccount.actions.activate'),
          onClick: () => handleActivate(serviceAccount.id),
          'data-testid': testIds.settings.serviceAccountManagementDetail('activate'),
        })
        .when(canDelete, {
          type: 'button',
          variant: 'danger',
          leftIcon: 'action/delete',
          title: i18n.t('general.actions.delete'),
          onClick: () => handleDelete(serviceAccount.id, serviceAccount.name),
          'data-testid': testIds.settings.serviceAccountManagementDetail('delete'),
        })
    : [];

  const tabs: SectionTab[] = isNotNil(serviceAccount)
    ? [
        {
          queryId: 'general',
          title: i18n.t('general.labels.general'),
          content: (
            <ServiceAccountGeneralTab
              serviceAccount={serviceAccount}
              data-testid={testIds.settings.serviceAccountManagementDetail('general')}
            />
          ),
        },
        {
          queryId: 'keys',
          title: i18n.t('entity.serviceAccount.labels.keys'),
          content: (
            <ServiceAccountKeysTab
              serviceAccountId={serviceAccount.id}
              canRotate={canRotate}
              canRevoke={canRevoke}
              data-testid={testIds.settings.serviceAccountManagementDetail('keys')}
            />
          ),
        },
        {
          queryId: 'request-log',
          title: i18n.t('entity.serviceAccount.labels.requestLog'),
          content: (
            <ServiceAccountRequestLogTab
              serviceAccountId={serviceAccount.id}
              data-testid={testIds.settings.serviceAccountManagementDetail('requestLog')}
            />
          ),
        },
        {
          queryId: 'audit-log',
          title: i18n.t('entity.serviceAccount.labels.auditLog'),
          content: (
            <ServiceAccountAuditLogTab
              serviceAccountId={serviceAccount.id}
              data-testid={testIds.settings.serviceAccountManagementDetail('auditLog')}
            />
          ),
        },
      ]
    : [];

  return (
    <SettingsTemplate
      isLoading={isLoading}
      isError={isError}
      header={{title: serviceAccount?.name, breadcrumbs, actions}}
      tabs={tabs}
      data-testid={testIds.settings.serviceAccountManagementDetail('page')}
    />
  );
}
